import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { alpha } from '@mui/material/styles';
import UserDashboard from 'layouts/UserDashboard';
import Container from 'components/Container';
import {
  getGoals,
  createGoal,
  updateGoal,
  deleteGoal,
  contributeToGoal,
  getMilestones,
  createMilestone,
  deleteMilestone,
} from './FinancialService';
import { listBusinesses } from '../Business/BusinessService';
import { GoalResponse, GoalRequest, GoalMilestone, UserBusinessResponse } from './types';
import FeatureGate from './FeatureGate';
import { FeatureKeys } from './FeatureKeys';

const C = {
  ink: '#17263d',
  muted: '#78869a',
  blue: '#1475d1',
  green: '#159b72',
  red: '#ff454d',
};

const gbp = (n: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(n);

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: '#3B82F6',
  COMPLETED: '#159b72',
  PAUSED: '#F59E0B',
  CANCELLED: '#6B7280',
};

const TYPE_LABELS: Record<string, string> = {
  SAVINGS: 'Savings',
  DEBT_PAYOFF: 'Debt payoff',
  PURCHASE: 'Purchase',
  EMERGENCY: 'Emergency fund',
  INVESTMENT: 'Investment',
  OTHER: 'Other',
};

function targetDateLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86_400_000);

  if (diff < 0) return `Overdue by ${Math.abs(diff)} days`;
  if (diff === 0) return 'Due today';
  if (diff <= 30) return `${diff} days remaining`;

  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const todayISO = () => new Date().toISOString().slice(0, 10);

// ─── GoalModal ────────────────────────────────────────────────────────────────

interface GoalModalProps {
  open: boolean;
  existing: GoalResponse | null;
  onClose: () => void;
  onSaved: () => void;
}

const GoalModal = ({ open, existing, onClose, onSaved }: GoalModalProps) => {
  const [name, setName] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [targetDate, setTargetDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [businesses, setBusinesses] = useState<UserBusinessResponse[]>([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState<number | ''>('');

  const monthlyPreview = useMemo(() => {
    const amt = parseFloat(targetAmount);
    if (!amt || amt <= 0 || !targetDate) return null;
    const target = new Date(targetDate + 'T00:00:00');
    const today = new Date();
    today.setDate(1);
    today.setHours(0, 0, 0, 0);
    const months =
      (target.getFullYear() - today.getFullYear()) * 12 +
      (target.getMonth() - today.getMonth());
    if (months <= 0) return null;
    return Math.ceil(amt / months);
  }, [targetAmount, targetDate]);

  useEffect(() => {
    if (open) {
      setName(existing?.name ?? '');
      setTargetAmount(existing?.targetAmount?.toString() ?? '');
      setTargetDate(existing?.targetDate ?? todayISO());
      setSelectedBusinessId(existing?.businessId ?? '');
      setError('');
      // Fetch active businesses for the selector
      listBusinesses()
        .then((res) =>
          setBusinesses(res.data.filter((b) => b.status === 'ACTIVE')),
        )
        .catch(() => setBusinesses([]));
    }
  }, [open, existing]);

  const handleSave = async () => {
    const amt = parseFloat(targetAmount);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!amt || amt <= 0) {
      setError('Enter a valid target amount greater than 0.');
      return;
    }
    if (!targetDate) {
      setError('Target date is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload: GoalRequest = {
        name: name.trim(),
        targetAmount: amt,
        targetDate,
        ...(selectedBusinessId !== '' && { businessId: selectedBusinessId as number }),
      };
      if (existing) {
        await updateGoal(existing.id, payload);
      } else {
        await createGoal(payload);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      if (err?.response?.status === 402) {
        setError(
          err.response.data?.message ??
          'Goal limit reached for your plan. Upgrade to create more goals.',
        );
      } else {
        setError('Could not save goal. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{existing ? 'Edit goal' : 'Add goal'}</DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: '12px !important',
        }}
      >
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Name"
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          autoFocus
        />
        <TextField
          label="Target amount (£)"
          type="number"
          size="small"
          value={targetAmount}
          onChange={(e) => setTargetAmount(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start">£</InputAdornment>,
          }}
          inputProps={{ min: 0, step: '0.01' }}
          fullWidth
        />
        <TextField
          label="Target date"
          type="date"
          size="small"
          value={targetDate}
          onChange={(e) => setTargetDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        {businesses.length > 0 && (
          <FormControl size="small" fullWidth>
            <InputLabel id="goal-business-label">Link to business (optional)</InputLabel>
            <Select
              labelId="goal-business-label"
              label="Link to business (optional)"
              value={selectedBusinessId}
              onChange={(e) => setSelectedBusinessId(e.target.value as number | '')}
            >
              <MenuItem value="">None</MenuItem>
              {businesses.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.businessName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {monthlyPreview != null && (
          <Box sx={{ bgcolor: alpha(C.blue, 0.05), borderRadius: 1.5, p: 1.5 }}>
            <Typography variant="caption" sx={{ color: C.muted }}>
              Suggested monthly contribution
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ color: C.blue, mt: 0.25 }}
            >
              {gbp(monthlyPreview)}/month
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: C.muted, display: 'block' }}
            >
              Target ÷ months remaining, rounded up
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving…' : existing ? 'Save changes' : 'Add goal'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── DeleteGoalDialog ─────────────────────────────────────────────────────────

interface DeleteGoalDialogProps {
  open: boolean;
  goal: GoalResponse | null;
  onClose: () => void;
  onDeleted: () => void;
}

const DeleteGoalDialog = ({
  open,
  goal,
  onClose,
  onDeleted,
}: DeleteGoalDialogProps) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    if (!goal) return;
    setDeleting(true);
    setError('');
    try {
      await deleteGoal(goal.id);
      onDeleted();
      onClose();
    } catch {
      setError('Could not delete goal. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete goal?</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography>
          This will permanently remove <strong>{goal?.name}</strong> and all
          saved progress. This cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={deleting}
        >
          {deleting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── ContributeModal ──────────────────────────────────────────────────────────

interface ContributeModalProps {
  open: boolean;
  goal: GoalResponse | null;
  onClose: () => void;
  onContributed: () => void;
}

const ContributeModal = ({
  open,
  goal,
  onClose,
  onContributed,
}: ContributeModalProps) => {
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setAmount('');
      setError('');
    }
  }, [open]);

  const handleContribute = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      setError('Enter a valid amount greater than 0.');
      return;
    }
    if (!goal) return;
    setSaving(true);
    setError('');
    try {
      await contributeToGoal(goal.id, amt);
      onContributed();
      onClose();
    } catch {
      setError('Could not record contribution. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const remaining = goal ? goal.targetAmount - goal.currentAmount : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add contribution</DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: '12px !important',
        }}
      >
        {error && <Alert severity="error">{error}</Alert>}
        {goal && (
          <Box sx={{ bgcolor: alpha(C.green, 0.06), borderRadius: 2, p: 1.5 }}>
            <Typography variant="body2" fontWeight={600} sx={{ color: C.ink }}>
              {goal.name}
            </Typography>
            <Typography variant="caption" sx={{ color: C.muted }}>
              {gbp(goal.currentAmount)} saved · {gbp(Math.max(remaining, 0))}{' '}
              remaining
            </Typography>
          </Box>
        )}
        <TextField
          label="Amount (£)"
          type="number"
          size="small"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start">£</InputAdornment>,
          }}
          inputProps={{ min: 0, step: '0.01' }}
          fullWidth
          autoFocus
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleContribute}
          variant="contained"
          disabled={saving}
          sx={{
            bgcolor: C.green,
            '&:hover': { bgcolor: alpha(C.green, 0.85) },
          }}
        >
          {saving ? 'Saving…' : 'Add contribution'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── GoalDetailDialog ─────────────────────────────────────────────────────────

interface GoalDetailDialogProps {
  open: boolean;
  goal: GoalResponse | null;
  onClose: () => void;
  onEdit: (g: GoalResponse) => void;
  onContribute: (g: GoalResponse) => void;
  onDelete: (g: GoalResponse) => void;
  onStatusChange: () => void;
}

const GoalDetailDialog = ({
  open,
  goal,
  onClose,
  onEdit,
  onContribute,
  onDelete,
  onStatusChange,
}: GoalDetailDialogProps) => {
  // ── Milestone state ──────────────────────────────────────────────────────
  const [milestones, setMilestones] = useState<GoalMilestone[]>([]);
  const [miLoading, setMiLoading] = useState(false);
  const [miTitle, setMiTitle] = useState('');
  const [miPercent, setMiPercent] = useState('');
  const [miSaving, setMiSaving] = useState(false);
  const [miError, setMiError] = useState('');

  // ── Status change state ───────────────────────────────────────────────────
  const [statusSaving, setStatusSaving] = useState(false);

  const goalId = goal?.id;

  useEffect(() => {
    if (!open || !goalId) {
      setMilestones([]);
      setMiTitle('');
      setMiPercent('');
      setMiError('');
      return;
    }
    setMiLoading(true);
    getMilestones(goalId)
      .then((res) => setMilestones(res.data))
      .catch(() => {})
      .finally(() => setMiLoading(false));
  }, [open, goalId]);

  const handleAddMilestone = async () => {
    const pct = parseFloat(miPercent);
    if (!miTitle.trim()) { setMiError('Title is required.'); return; }
    if (!pct || pct < 1 || pct > 100) { setMiError('Percent must be 1–100.'); return; }
    if (!goalId) return;
    setMiSaving(true);
    setMiError('');
    try {
      const res = await createMilestone(goalId, { title: miTitle.trim(), targetPercent: pct });
      setMilestones((prev) =>
        [...prev, res.data].sort((a, b) => a.targetPercent - b.targetPercent),
      );
      setMiTitle('');
      setMiPercent('');
    } catch (err: any) {
      setMiError(err?.response?.data?.message ?? 'Could not add milestone.');
    } finally {
      setMiSaving(false);
    }
  };

  const handleDeleteMilestone = async (milestoneId: number) => {
    if (!goalId) return;
    try {
      await deleteMilestone(goalId, milestoneId);
      setMilestones((prev) => prev.filter((m) => m.id !== milestoneId));
    } catch {
      setMiError('Could not remove milestone.');
    }
  };

  const handleMarkComplete = async () => {
    if (!goal) return;
    setStatusSaving(true);
    try {
      await updateGoal(goal.id, {
        name: goal.name,
        targetAmount: goal.targetAmount,
        targetDate: goal.targetDate ?? '',
        linkedBucket: goal.linkedBucket ?? undefined,
        icon: goal.icon ?? undefined,
        businessId: goal.businessId,
        status: 'COMPLETED',
      });
      onStatusChange();
      onClose();
    } finally {
      setStatusSaving(false);
    }
  };

  const handleReopen = async () => {
    if (!goal) return;
    setStatusSaving(true);
    try {
      await updateGoal(goal.id, {
        name: goal.name,
        targetAmount: goal.targetAmount,
        targetDate: goal.targetDate ?? '',
        linkedBucket: goal.linkedBucket ?? undefined,
        icon: goal.icon ?? undefined,
        businessId: goal.businessId,
        status: 'ACTIVE',
      });
      onStatusChange();
      onClose();
    } finally {
      setStatusSaving(false);
    }
  };

  if (!goal) return null;

  const statusColor = STATUS_COLORS[goal.status] ?? '#6B7280';
  const isActive = goal.status === 'ACTIVE';
  const pct = Math.min(Math.round(goal.progressPercent), 100);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: C.ink }}>
              {goal.name}
            </Typography>
            <Typography variant="caption" sx={{ color: C.muted }}>
              {TYPE_LABELS[goal.goalType] ?? goal.goalType}
            </Typography>
          </Box>
          <Chip
            label={goal.status.charAt(0) + goal.status.slice(1).toLowerCase()}
            size="small"
            sx={{
              bgcolor: alpha(statusColor, 0.1),
              color: statusColor,
              fontWeight: 700,
              fontSize: '0.7rem',
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Progress bar */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
          >
            <Typography variant="body2" sx={{ color: C.muted }}>
              {gbp(goal.currentAmount)} saved
            </Typography>
            <Typography variant="body2" fontWeight={700} sx={{ color: C.ink }}>
              {pct}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              height: 10,
              borderRadius: 5,
              bgcolor: alpha(statusColor, 0.12),
              '& .MuiLinearProgress-bar': {
                bgcolor: goal.progressPercent > 100 ? C.green : statusColor,
                borderRadius: 5,
              },
            }}
          />
        </Box>

        {/* KPI row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={4}>
            <Box
              sx={{
                textAlign: 'center',
                p: 1.5,
                bgcolor: alpha(C.green, 0.06),
                borderRadius: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: C.muted, display: 'block' }}
              >
                Saved
              </Typography>
              <Typography variant="h6" fontWeight={700} sx={{ color: C.green }}>
                {gbp(goal.currentAmount)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box
              sx={{
                textAlign: 'center',
                p: 1.5,
                bgcolor: alpha(C.blue, 0.06),
                borderRadius: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: C.muted, display: 'block' }}
              >
                Remaining
              </Typography>
              <Typography variant="h6" fontWeight={700} sx={{ color: C.blue }}>
                {gbp(Math.max(goal.amountRemaining, 0))}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box
              sx={{
                textAlign: 'center',
                p: 1.5,
                bgcolor: alpha(C.ink, 0.04),
                borderRadius: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: C.muted, display: 'block' }}
              >
                Target
              </Typography>
              <Typography variant="h6" fontWeight={700} sx={{ color: C.ink }}>
                {gbp(goal.targetAmount)}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Monthly pace */}
        {goal.monthlyRequired != null &&
          goal.monthlyRequired > 0 &&
          isActive && (
          <Box
            sx={{
              bgcolor: alpha(C.blue, 0.05),
              borderRadius: 2,
              p: 2,
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ color: C.ink }}
            >
              {gbp(goal.monthlyRequired)}/month suggested
            </Typography>
            <Typography variant="caption" sx={{ color: C.muted }}>
                Based on {gbp(Math.max(goal.amountRemaining, 0))} remaining
              {goal.daysRemaining != null
                ? ` over ~${Math.ceil(goal.daysRemaining / 30)} months`
                : ''}
                . Formula: amount remaining ÷ months to target date, rounded up.
            </Typography>
          </Box>
        )}

        {/* Target date row */}
        {goal.targetDate && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" sx={{ color: C.muted }}>
              Target date
            </Typography>
            <Box sx={{ textAlign: 'right' }}>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ color: C.ink }}
              >
                {new Date(goal.targetDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </Typography>
              <Typography variant="caption" sx={{ color: C.muted }}>
                {targetDateLabel(goal.targetDate)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Linked bucket */}
        {goal.linkedBucket && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" sx={{ color: C.muted }}>
              Budget bucket
            </Typography>
            <Chip
              label={
                goal.linkedBucket.charAt(0) +
                goal.linkedBucket.slice(1).toLowerCase()
              }
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.68rem', textTransform: 'capitalize' }}
            />
          </Box>
        )}

        {/* ── Milestones ────────────────────────────────────────────────── */}
        <Box sx={{ mt: 2.5 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Typography variant="body2" fontWeight={700} sx={{ color: C.ink }}>
              Milestones
            </Typography>
            <Typography variant="caption" sx={{ color: C.muted }}>
              {milestones.length}/10
            </Typography>
          </Box>

          {miLoading ? (
            <Skeleton height={32} sx={{ borderRadius: 1 }} />
          ) : milestones.length === 0 ? (
            <Typography
              variant="caption"
              sx={{ color: C.muted, fontStyle: 'italic' }}
            >
              No milestones yet.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {milestones.map((m) => (
                <Box
                  key={m.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 1.5,
                    bgcolor: alpha(m.reached ? C.green : C.blue, 0.06),
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ flex: 1, color: C.ink, fontWeight: 500 }}
                  >
                    {m.title}
                  </Typography>
                  <Chip
                    label={`${m.targetPercent}%`}
                    size="small"
                    sx={{
                      fontSize: '0.65rem',
                      height: 20,
                      bgcolor: m.reached
                        ? alpha(C.green, 0.15)
                        : alpha(C.blue, 0.1),
                      color: m.reached ? C.green : C.blue,
                    }}
                  />
                  {m.reached && (
                    <Chip
                      label="Reached"
                      size="small"
                      sx={{
                        fontSize: '0.65rem',
                        height: 20,
                        bgcolor: alpha(C.green, 0.1),
                        color: C.green,
                      }}
                    />
                  )}
                  <Tooltip title="Remove milestone">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteMilestone(m.id)}
                      sx={{ color: C.muted }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}

          {/* Add milestone form — active goals only, max 10 */}
          {isActive && milestones.length < 10 && (
            <Box sx={{ mt: 1.5 }}>
              {miError && (
                <Alert
                  severity="error"
                  sx={{ mb: 1 }}
                  onClose={() => setMiError('')}
                >
                  {miError}
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  placeholder="Milestone name"
                  size="small"
                  value={miTitle}
                  onChange={(e) => setMiTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddMilestone();
                  }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  placeholder="%"
                  type="number"
                  size="small"
                  value={miPercent}
                  onChange={(e) => setMiPercent(e.target.value)}
                  inputProps={{ min: 1, max: 100, step: 1 }}
                  sx={{ width: 72 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAddMilestone}
                  disabled={miSaving}
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {miSaving ? '…' : 'Add'}
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        {/* Progress source note */}
        <Typography
          variant="caption"
          sx={{ color: C.muted, display: 'block', mt: 2, fontStyle: 'italic' }}
        >
          Progress reflects manually recorded contributions. Transaction-linked
          goal tracking is not yet available.
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{ px: 3, pb: 2.5, gap: 1, justifyContent: 'space-between' }}
      >
        <Button
          onClick={() => {
            onClose();
            onDelete(goal);
          }}
          variant="outlined"
          color="error"
          size="small"
        >
          Delete
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={() => {
              onClose();
              onEdit(goal);
            }}
            variant="outlined"
          >
            Edit
          </Button>
          {isActive && (
            <Button
              onClick={() => {
                onClose();
                onContribute(goal);
              }}
              variant="contained"
              sx={{
                bgcolor: C.green,
                '&:hover': { bgcolor: alpha(C.green, 0.85) },
              }}
            >
              Add contribution
            </Button>
          )}
          {isActive && (
            <Button
              onClick={handleMarkComplete}
              disabled={statusSaving}
              variant="outlined"
              color="success"
            >
              Mark as Complete
            </Button>
          )}
          {goal.status === 'COMPLETED' && (
            <Button
              onClick={handleReopen}
              disabled={statusSaving}
              variant="outlined"
            >
              Reopen
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

// ─── GoalsPage ────────────────────────────────────────────────────────────────

const GoalsPage = (): JSX.Element => {
  const [rows, setRows] = useState<GoalResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'ALL' | 'ACTIVE' | 'COMPLETED'>('ALL');

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<GoalResponse | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<GoalResponse | null>(null);
  const [contributeOpen, setContributeOpen] = useState(false);
  const [contributeGoal, setContributeGoal] = useState<GoalResponse | null>(
    null,
  );
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailGoal, setDetailGoal] = useState<GoalResponse | null>(null);

  const load = () => {
    setLoading(true);
    setError('');
    getGoals()
      .then((res) => setRows(res.data))
      .catch(() => setError('Could not load goals.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (filter === 'ALL') return rows;
    return rows.filter((r) => r.status === filter);
  }, [rows, filter]);

  const activeGoals = rows.filter((r) => r.status === 'ACTIVE');
  const totalTarget = activeGoals.reduce((s, r) => s + r.targetAmount, 0);
  const totalSaved = activeGoals.reduce((s, r) => s + r.currentAmount, 0);

  const openAdd = () => {
    setEditTarget(null);
    setModalOpen(true);
  };
  const openEdit = (g: GoalResponse) => {
    setEditTarget(g);
    setModalOpen(true);
  };
  const openDel = (g: GoalResponse) => {
    setDeleteTarget(g);
    setDeleteOpen(true);
  };
  const openContribute = (g: GoalResponse) => {
    setContributeGoal(g);
    setContributeOpen(true);
  };
  const openDetail = (g: GoalResponse) => {
    setDetailGoal(g);
    setDetailOpen(true);
  };

  return (
    <UserDashboard>
      <FeatureGate featureKey={FeatureKeys.GOALS_CORE}>
        <Box
          sx={{
            background: `linear-gradient(175deg, ${alpha(C.blue, 0.05)} 0%, transparent 40%)`,
            minHeight: '100vh',
            py: 4,
          }}
        >
          <Container>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                mb: 3,
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h4" fontWeight={700} sx={{ color: C.ink }}>
                  Goals
                </Typography>
                <Typography variant="body2" sx={{ color: C.muted }}>
                  Savings targets and progress
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {!loading && totalTarget > 0 && (
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography
                      variant="caption"
                      sx={{ color: C.muted, display: 'block' }}
                    >
                      Active goals
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ color: C.ink }}
                    >
                      {gbp(totalSaved)} / {gbp(totalTarget)}
                    </Typography>
                  </Box>
                )}
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openAdd}
                >
                  Add goal
                </Button>
              </Box>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {/* Filter chips */}
            {!loading && (
              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                {(['ALL', 'ACTIVE', 'COMPLETED'] as const).map((s) => (
                  <Chip
                    key={s}
                    label={
                      s === 'ALL'
                        ? 'All'
                        : s.charAt(0) + s.slice(1).toLowerCase()
                    }
                    onClick={() => setFilter(s)}
                    variant={filter === s ? 'filled' : 'outlined'}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      ...(filter === s && {
                        bgcolor: alpha(C.blue, 0.12),
                        color: C.blue,
                        borderColor: 'transparent',
                      }),
                    }}
                  />
                ))}
              </Box>
            )}

            {loading ? (
              <Grid container spacing={2}>
                {Array.from({ length: 3 }).map((_, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Skeleton
                      variant="rectangular"
                      height={160}
                      sx={{ borderRadius: 2 }}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : filtered.length === 0 ? (
              <Card>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Typography sx={{ color: C.muted, mb: 2 }}>
                    No goals found.
                  </Typography>
                  {filter === 'ALL' && (
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={openAdd}
                    >
                      Add your first goal
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Grid container spacing={2}>
                {filtered.map((g) => {
                  const statusColor = STATUS_COLORS[g.status] ?? '#6B7280';
                  const isOver = g.progressPercent > 100;
                  const isActive = g.status === 'ACTIVE';
                  return (
                    <Grid item xs={12} sm={6} md={4} key={g.id}>
                      <Card
                        sx={{ height: '100%', cursor: 'pointer' }}
                        onClick={() => openDetail(g)}
                      >
                        <CardContent sx={{ p: 2.5 }}>
                          {/* Title row */}
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              mb: 1,
                            }}
                          >
                            <Box sx={{ flex: 1, mr: 1 }}>
                              <Typography
                                variant="body1"
                                fontWeight={600}
                                sx={{ color: C.ink }}
                              >
                                {g.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: C.muted }}
                              >
                                {TYPE_LABELS[g.goalType] ?? g.goalType}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <Chip
                                label={
                                  g.status.charAt(0) +
                                  g.status.slice(1).toLowerCase()
                                }
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  bgcolor: alpha(statusColor, 0.1),
                                  color: statusColor,
                                }}
                              />
                              {isActive && (
                                <Tooltip title="Add contribution">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openContribute(g);
                                    }}
                                    sx={{ color: C.green }}
                                  >
                                    <AddCircleOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEdit(g);
                                  }}
                                  sx={{ color: C.muted }}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDel(g);
                                  }}
                                  sx={{ color: C.muted }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>

                          {/* Progress bar */}
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(g.progressPercent, 100)}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              mb: 1,
                              bgcolor: alpha(statusColor, 0.12),
                              '& .MuiLinearProgress-bar': {
                                bgcolor: isOver ? C.green : statusColor,
                                borderRadius: 3,
                              },
                            }}
                          />

                          {/* Amounts */}
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Typography variant="body2" sx={{ color: C.muted }}>
                              {gbp(g.currentAmount)}
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{ color: C.ink }}
                            >
                              {Math.round(g.progressPercent)}%
                            </Typography>
                            <Typography variant="body2" sx={{ color: C.muted }}>
                              {gbp(g.targetAmount)}
                            </Typography>
                          </Box>

                          {/* Monthly required */}
                          {g.monthlyRequired != null &&
                            g.monthlyRequired > 0 &&
                            isActive && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: C.muted,
                                mt: 0.5,
                                display: 'block',
                              }}
                            >
                              {gbp(g.monthlyRequired)}/mo to reach target
                            </Typography>
                          )}

                          {/* Target date */}
                          {g.targetDate && (
                            <Typography
                              variant="caption"
                              sx={{ color: C.muted, mt: 0.5, display: 'block' }}
                            >
                              {targetDateLabel(g.targetDate)}
                            </Typography>
                          )}

                          {/* Business chip */}
                          {g.businessName && (
                            <Box sx={{ mt: 0.75 }}>
                              <Chip
                                label={g.businessName}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.65rem', height: 20 }}
                              />
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Container>
        </Box>
      </FeatureGate>

      <GoalModal
        open={modalOpen}
        existing={editTarget}
        onClose={() => setModalOpen(false)}
        onSaved={load}
      />
      <DeleteGoalDialog
        open={deleteOpen}
        goal={deleteTarget}
        onClose={() => setDeleteOpen(false)}
        onDeleted={load}
      />
      <ContributeModal
        open={contributeOpen}
        goal={contributeGoal}
        onClose={() => setContributeOpen(false)}
        onContributed={load}
      />
      <GoalDetailDialog
        open={detailOpen}
        goal={detailGoal}
        onClose={() => setDetailOpen(false)}
        onEdit={(g) => openEdit(g)}
        onContribute={(g) => openContribute(g)}
        onDelete={(g) => openDel(g)}
        onStatusChange={load}
      />
    </UserDashboard>
  );
};

export default GoalsPage;
